// src/oura/components/aeo/AssessmentForm.tsx
//
// AC: "Create form: domain, business_name, category, anchors (primary
// required), services (required), languages, optional competitors;
// client-validates primary + services non-empty before submit." (#3065)
// AC: "On 202 → polling view; on 409 → link to the existing in-flight
// assessment (no duplicate submit)."

import { useEffect, useState, type ReactNode } from "react";
import { Loader2Icon, ScanLineIcon } from "lucide-react";
import { Card, CardBody, CardHeader } from "../../primitives/Card";
import { Button } from "../../primitives/Button";
import { cn } from "../../lib/cn";
import { createAssessment, type AeoCreateAssessmentRequest } from "@service/aeo";
import type { PrescreenPrefill } from "./PrescreenCard";

const INPUT =
    "h-9 w-full rounded-md border border-gray-300 px-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-blue-500";

function Field({
    label,
    required,
    hint,
    children,
}: {
    label: string;
    required?: boolean;
    hint?: string;
    children: ReactNode;
}) {
    return (
        <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-900">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            {children}
            {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
        </div>
    );
}

const csv = (s: string) => s.split(",").map((v) => v.trim()).filter(Boolean);

export function AssessmentForm({
    prefill,
    onCreated,
    onDuplicate,
}: {
    prefill?: PrescreenPrefill | null;
    /** 202 accepted → hand up the new id (+ the domain actually submitted) so the screen can switch to polling. */
    onCreated: (assessmentId: string, domain: string) => void;
    /** 409 → hand up the EXISTING in-flight id; don't double-submit. */
    onDuplicate: (assessmentId: string, domain: string) => void;
}) {
    const [domain, setDomain] = useState(prefill?.domain ?? "");
    const [businessName, setBusinessName] = useState(prefill?.business_name ?? "");
    const [category, setCategory] = useState("");
    const [anchors, setAnchors] = useState(prefill?.anchors_primary ?? "");
    const [adjacent, setAdjacent] = useState("");
    const [city, setCity] = useState("");
    const [services, setServices] = useState("");
    const [languages, setLanguages] = useState("en");
    const [competitors, setCompetitors] = useState("");
    const [touched, setTouched] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    // A prescreen "run full assessment" click should prefill the fields it knows.
    useEffect(() => {
        if (!prefill) return;
        setDomain(prefill.domain);
        setBusinessName(prefill.business_name);
        setAnchors(prefill.anchors_primary);
    }, [prefill]);

    const errors = {
        domain: !domain.trim() ? "Domain is required" : null,
        businessName: !businessName.trim() ? "Business name is required" : null,
        anchors: csv(anchors).length === 0 ? "At least one anchor area is required" : null,
        services: csv(services).length === 0 ? "At least one service is required" : null,
    };
    const valid = !errors.domain && !errors.businessName && !errors.anchors && !errors.services;

    const submit = async () => {
        setTouched(true);
        if (!valid) return;
        setSubmitting(true);
        setFormError(null);

        const input: AeoCreateAssessmentRequest = {
            domain: domain.trim(),
            business_name: businessName.trim(),
            category: category.trim() || undefined,
            anchors: {
                primary: csv(anchors),
                adjacent: csv(adjacent),
                city: city.trim() || undefined,
            },
            services: csv(services),
            languages: csv(languages),
            competitors: csv(competitors),
        };

        try {
            const result = await createAssessment(input);
            if (result.ok) {
                onCreated(result.data.assessment_id, input.domain);
                return;
            }
            if (result.kind === "duplicate") {
                onDuplicate(result.assessment_id, input.domain);
                return;
            }
            if (result.kind === "validation") {
                setFormError(result.errors.join(" "));
                return;
            }
            setFormError(result.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card>
            <CardHeader
                title="New assessment"
                description="Anchors and services are the biggest lever on question quality — the closer they match how locals actually search, the truer the score."
                icon={ScanLineIcon}
            />
            <CardBody className="space-y-4 pt-2">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Company domain" required>
                        <input
                            className={cn(INPUT, "font-mono", touched && errors.domain && "border-rose-400")}
                            placeholder="dentalhome.com.my"
                            value={domain}
                            onChange={(e) => setDomain(e.target.value)}
                        />
                    </Field>
                    <Field label="Business name" required>
                        <input
                            className={cn(INPUT, touched && errors.businessName && "border-rose-400")}
                            placeholder="Dental Home"
                            value={businessName}
                            onChange={(e) => setBusinessName(e.target.value)}
                        />
                    </Field>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Category">
                        <input
                            className={INPUT}
                            placeholder="dental clinic"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                        />
                    </Field>
                    <Field label="City">
                        <input
                            className={INPUT}
                            placeholder="Kuala Lumpur"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                        />
                    </Field>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Anchor areas" required hint="Comma-separated, e.g. Mont Kiara, KLGCC.">
                        <input
                            className={cn(INPUT, touched && errors.anchors && "border-rose-400")}
                            placeholder="Mont Kiara, KLGCC"
                            value={anchors}
                            onChange={(e) => setAnchors(e.target.value)}
                        />
                    </Field>
                    <Field label="Adjacent areas" hint="Optional, comma-separated.">
                        <input
                            className={INPUT}
                            placeholder="Sri Hartamas"
                            value={adjacent}
                            onChange={(e) => setAdjacent(e.target.value)}
                        />
                    </Field>
                </div>
                <Field label="Services" required hint="Comma-separated, e.g. veneers, Invisalign.">
                    <input
                        className={cn(INPUT, touched && errors.services && "border-rose-400")}
                        placeholder="veneers, Invisalign"
                        value={services}
                        onChange={(e) => setServices(e.target.value)}
                    />
                </Field>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Languages" hint="Comma-separated language codes.">
                        <input className={INPUT} value={languages} onChange={(e) => setLanguages(e.target.value)} />
                    </Field>
                    <Field label="Competitors" hint="Optional, comma-separated domains.">
                        <input
                            className={INPUT}
                            placeholder="rivalclinic.com.my"
                            value={competitors}
                            onChange={(e) => setCompetitors(e.target.value)}
                        />
                    </Field>
                </div>

                {touched && !valid && (
                    <p className="text-xs text-rose-600">
                        {errors.domain ?? errors.businessName ?? errors.anchors ?? errors.services}
                    </p>
                )}
                {formError && (
                    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-700">
                        {formError}
                    </div>
                )}

                <div className="flex justify-end">
                    <Button disabled={submitting} onClick={() => void submit()}>
                        {submitting ? <Loader2Icon className="h-4 w-4 animate-spin" /> : <ScanLineIcon className="h-4 w-4" />}
                        Start assessment
                    </Button>
                </div>
            </CardBody>
        </Card>
    );
}
